/**
 * Сборщик Redux-совместимой цепочки middleware для Zustand.
 * Создаёт обёртку над dispatch, пропуская экшены через цепочку middleware.
 *
 * @param api — { getState, dispatch }; при непустой цепочке dispatch присваивается результатом
 * @param rawDispatch — «сырой» dispatch, применяющий редьюсер
 * @param middlewares — цепочка middleware (может быть пустой)
 * @returns обёрнутый dispatch
 */
import type { ActionFromDispatch, DispatchLike } from './store.types';
import type { Middleware, MiddlewareAPI } from './store.types';

/**
 * Собирает цепочку middleware и возвращает обёрнутый dispatch.
 *
 * @template S — тип состояния store
 * @template D — тип dispatch
 * @param {MiddlewareAPI<D, S>} api — API для middleware
 * @param {D} rawDispatch — исходный dispatch Zustand
 * @param {...Middleware<unknown, S, D>[]} middlewares — middleware в порядке «снаружи → внутрь»
 * @returns {D} итоговый dispatch
 */
export function applyMiddleware<S, D extends DispatchLike>(
  api: MiddlewareAPI<D, S>,
  rawDispatch: D,
  ...middlewares: Middleware<unknown, S, D>[]
): D {
  type Action = ActionFromDispatch<D>;

  if (middlewares.length === 0) {
    (api as { dispatch: D }).dispatch = rawDispatch;
    return rawDispatch;
  }

  const chain = middlewares.reduceRight<(action: Action) => unknown>(
    (next, middleware) => {
      const m = middleware(api as MiddlewareAPI<D, S>);
      return m(next as D);
    },
    rawDispatch as (action: Action) => unknown,
  );
  (api as { dispatch: D }).dispatch = chain as D;
  return chain as D;
}
